/**
 * The read-only closed-thread transcript (README §8; v2.3 §10-11): where a
 * closed thread opens, and where a Halo deep link lands once the thread it
 * points at has closed (`TranscriptPage` decides that; this component only
 * renders the result).
 *
 * Deliberately offers nothing that could write to the thread — no composer,
 * no operation button anywhere. Back navigation uses `<Link>` (a real nav,
 * not an action) and the ticket chip is a plain external anchor, exactly
 * like the live pane's own ticket chip (`ThreadHeader`) — reused here rather
 * than re-derived so a conversation reads the same closed as it did live.
 */
import { Link } from 'react-router-dom'
import type { ThreadDetail } from '@communicator/shared'
import { Card, Chip } from '../ui'
import { Transcript, haloTicketUrl } from '../thread'
import { openedByLine } from './format'
import { selectPref, selectTimeZone, useStore } from '../../store'

export interface ReadOnlyTranscriptProps {
  thread: ThreadDetail
  cfg: Record<string, unknown>
}

export function ReadOnlyTranscript({ thread, cfg }: ReadOnlyTranscriptProps) {
  const linked = thread.ticket !== null
  // 2026-08-24 design: the "READ ONLY" banner's opened-at line stays on the
  // org-wide `v.tz` (via `selectTimeZone(cfg)` below — a Halo note is read by
  // anyone, in our time), but the embedded live Transcript renders the same
  // per-user message stamps the live pane does, so a closed thread reads the
  // way it did while it was open.
  const tz = useStore((s) => selectPref(s.prefs, 'p.tz', 'auto'))
  // `null` when Halo's host is not configured yet — see haloTicketUrl's own
  // doc comment (task-42, bug 1). Never falls back to a link that resolves
  // against this app's own origin.
  const ticketHref = linked ? haloTicketUrl(cfg, thread.ticket as string) : null

  return (
    <div data-testid="transcript-page" className="flex h-full min-h-0 flex-col overflow-y-auto bg-page">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-subtle bg-sidebar px-4 py-[9px]">
        <nav className="flex items-center gap-3 text-[12px]" aria-label="Back">
          <Link to="/history" data-testid="back-to-history" className="text-cc-blue-text underline">
            Back to History
          </Link>
          <span className="text-lo">·</span>
          <Link to="/" data-testid="back-to-console" className="text-cc-blue-text underline">
            Back to Console
          </Link>
        </nav>

        <div className="flex min-w-[220px] flex-[1_1_220px] flex-wrap items-center gap-2" data-testid="transcript-identity">
          <span className="text-[14px] font-semibold text-hi">{thread.who}</span>
          <span className="text-[12px] text-mid">
            {thread.client ?? 'No known company'} · {thread.endpoint ?? 'unmapped handle'}
          </span>
        </div>

        <div className="ml-auto flex flex-none items-center gap-2">
          {linked && ticketHref ? (
            <a
              href={ticketHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ticket-chip"
              className="whitespace-nowrap rounded-[5px] bg-elevated px-2 py-1 font-mono text-[12px] text-hi"
            >
              {thread.ticket} ↗
            </a>
          ) : linked ? (
            <span
              data-testid="ticket-chip"
              title="This Halo link can't be built yet — the Halo host isn't configured."
              className="whitespace-nowrap rounded-[5px] bg-elevated px-2 py-1 font-mono text-[12px] text-lo"
            >
              {thread.ticket}
            </span>
          ) : (
            <span className="whitespace-nowrap rounded-[5px] bg-elevated px-2 py-1 text-[11px] text-mid">No ticket</span>
          )}
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[760px] flex-1 flex-col gap-4 p-4">
        <Card data-testid="readonly-banner" className="flex flex-col gap-1 p-4">
          <div className="flex items-center gap-2">
            <Chip tone="neutral">READ ONLY · CLOSE IS TERMINAL</Chip>
          </div>
          <p className="text-[13px] leading-[1.6] text-mid">{openedByLine(thread.owner, thread.openedAt, selectTimeZone(cfg))}</p>
        </Card>

        {linked ? (
          <Card data-testid="close-note-card" className="flex flex-col gap-1 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.09em] text-lo">
              Close note written to {thread.ticket}
            </div>
            <p className="text-[13px] leading-[1.6] text-hi">
              {thread.closeNote ?? 'No close note was recorded for this thread.'}
            </p>
          </Card>
        ) : (
          <Card data-testid="unlinked-notice" className="flex flex-col gap-1 border-dashed p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.09em] text-lo">No ticket</div>
            <p className="text-[13px] leading-[1.6] text-mid">
              This thread was never linked to a ticket. Nothing about it was ever written to Halo.
            </p>
          </Card>
        )}

        <Card className="flex min-h-0 flex-1 flex-col p-0">
          {/* No operation buttons anywhere on this page — close is terminal, so
              an unsent message still shows "not sent", never a Retry. */}
          <Transcript messages={thread.msgs} readOnly tz={tz} />
        </Card>
      </div>
    </div>
  )
}
